/**
 * The markdown screen's pure logic — no React, no `window`, no network.
 *
 * Everything here is a lookup or a fold over rows the platform published. Nothing
 * computes a planning measure: G11 gives realised price, contribution and the frontier
 * exactly one producer, and a browser that re-derived any of them would be the second.
 * The folds below are counts and sums over one page of an answer, which is the same
 * thing `MarkdownScreen` has always done with the price-action totals.
 *
 * It is a module rather than a handful of helpers inside the screen because the screen
 * cannot be rendered without a `QueryClientProvider`, three stubbed reads and a URL,
 * and none of that is needed to assert that an unrecognised `position_state` is
 * labelled rather than dropped. `markdown.test.ts` asserts on these directly.
 */

import type {
  AssembledFrontierPoint,
  AssembledMarkdownPath,
  MarkdownStep,
  ObjectiveKind,
  PenaltyBasis,
  StockPositionSummary,
} from '@/api/generated';
import type { ProblemDetails } from '@/api';

/**
 * How many options one view will solve at once.
 *
 * Here rather than in the panel because the export resolver needs the same bound: an
 * export that answered for more options than the screen did would not be the view the
 * planner is looking at, which is the whole of G2.4.
 */
export const BATCH_LIMIT = 300;

/**
 * The narrowest machine-readable code a failure carries, or `null`.
 *
 * Three members, in the order they narrow. `missingInput` names the absent feed on an
 * inputs-unavailable refusal and `planningCode` names the rule on a planning rejection;
 * RFC 9457 extension members exist so that a category and its reason need not be the same
 * string. `code` is the category and is the answer only when neither extension is present.
 *
 * Not folded into `problemMessage`: that function resolves a catalogue key, and widening
 * it to prefer `missingInput` would change the sentence every other screen shows for the
 * same refusal. This reads the document for display beside the sentence, which is a
 * different question.
 */
export function refusalCode(error: unknown): string | null {
  const problem = (error as { problem?: ProblemDetails } | null)?.problem;
  if (problem === undefined) return null;
  const missing = problem['missingInput'];
  if (typeof missing === 'string' && missing !== '') return missing;
  const planning = problem['planningCode'];
  if (typeof planning === 'string' && planning !== '') return planning;
  return problem.code;
}

// ── the objective vocabulary ─────────────────────────────────────────────────

/**
 * The two objectives the contract publishes.
 *
 * Exported as a tuple so the control offers exactly what the generated union permits:
 * a picker built from a hand-typed list would drift from the contract the first time a
 * third kind landed, and the drift would be silent in both directions — an offered kind
 * the platform refuses, or a permitted kind nobody can choose.
 */
export const OBJECTIVE_KINDS: readonly ObjectiveKind[] = ['terminal_constraint', 'leftover_penalty'];

/** Which units a leftover penalty charges for. */
export const PENALTY_BASES: readonly PenaltyBasis[] = ['all_leftover', 'above_target'];

export function isObjectiveKind(value: string): value is ObjectiveKind {
  return (OBJECTIVE_KINDS as readonly string[]).includes(value);
}

export function isPenaltyBasis(value: string): value is PenaltyBasis {
  return (PENALTY_BASES as readonly string[]).includes(value);
}

/**
 * Catalogue keys for the objective vocabulary, as a lookup rather than by interpolation.
 *
 * `tools/i18n-key-coverage.mjs` extracts a key it can see as a literal, and enumerates an
 * interpolated family only if the family is registered in the tool — which is outside this
 * chapter's namespace. A table of literals needs no registration and is greppable besides.
 * The `unrecognised` member is the house convention: a kind this build has never heard of
 * is LABELLED as unrecognised rather than dropped or defaulted into a real member, because
 * silently rendering a new objective as `terminal_constraint` would misattribute the whole
 * recommendation.
 */
const OBJECTIVE_KIND_KEY: Readonly<Record<string, string>> = {
  terminal_constraint: 'inseason.objective.terminalConstraint',
  leftover_penalty: 'inseason.objective.leftoverPenalty',
};

export const UNRECOGNISED_OBJECTIVE_KEY = 'inseason.objective.unrecognised';

export function toObjectiveKindKey(kind: string): string {
  return OBJECTIVE_KIND_KEY[kind] ?? UNRECOGNISED_OBJECTIVE_KEY;
}

const PENALTY_BASIS_KEY: Readonly<Record<string, string>> = {
  all_leftover: 'inseason.penaltyBasis.allLeftover',
  above_target: 'inseason.penaltyBasis.aboveTarget',
};

export const UNRECOGNISED_PENALTY_BASIS_KEY = 'inseason.penaltyBasis.unrecognised';

export function toPenaltyBasisKey(basis: string): string {
  return PENALTY_BASIS_KEY[basis] ?? UNRECOGNISED_PENALTY_BASIS_KEY;
}

// ── feasibility ──────────────────────────────────────────────────────────────

/**
 * Feasibility as a status level, so it can never be colour alone (design/09 §12.6).
 *
 * `StatusIndicator` derives its icon from the level and requires a label key, so a
 * caller that passes one of these gets the icon and the word without being able to
 * forget either.
 */
export function feasibilityLevel(isFeasible: boolean): 'good' | 'critical' {
  return isFeasible ? 'good' : 'critical';
}

export function feasibilityLabelKey(isFeasible: boolean): string {
  return isFeasible ? 'inseason.markdown.feasible' : 'inseason.markdown.infeasible';
}

/**
 * By how much the solved path misses chapter 05's terminal target, in units.
 *
 * A subtraction of two published fields, not a re-derivation: both `terminal_units` and
 * `terminal_target_units` are on the response, and neither is recomputed here. Positive
 * means the path leaves MORE stock than the target allows, which is why the caller renders
 * it against `lower-is-better` polarity — a positive residual is the bad direction, and
 * design/09 §5.3 forbids assuming that anywhere but at the measure.
 */
export function terminalShortfall(path: {
  readonly terminal_units: number;
  readonly terminal_target_units: number;
}): number {
  return path.terminal_units - path.terminal_target_units;
}

/**
 * The carrying cost the path itself charges, summed over its weeks.
 *
 * `MarkdownStep.carrying_cost` is a published field on every week and was rendered
 * nowhere. The total is a sum over one answer's own rows — the same shape as the
 * price-action totals the actions panel has always shown — and it is deliberately NOT
 * presented as the cost of waiting a week: that would be the difference between two
 * solves, and re-solving the problem in the browser is the G11 violation ADR 0053
 * records. What a planner can read off this is what holding the position for the
 * horizon they chose costs inside the recommendation they are looking at.
 */
export function totalCarryingCost(steps: readonly MarkdownStep[]): number {
  return steps.reduce((total, step) => total + step.carrying_cost, 0);
}

/** The margin the path expects, summed over its weeks. Same argument as above. */
export function totalMargin(steps: readonly MarkdownStep[]): number {
  return steps.reduce((total, step) => total + step.margin, 0);
}

// ── the frontier ─────────────────────────────────────────────────────────────

/**
 * The frontier's points, ordered by cost of carry, as the chart needs them.
 *
 * Sorted rather than trusted in arrival order: the category axis declares its order and
 * `assertChartSpec` checks every event line against it, so a frontier that arrived
 * unsorted would draw a line that crosses itself. Sorting a list of published rows is not
 * a computation over them.
 */
export function orderedFrontier(
  points: readonly AssembledFrontierPoint[],
): readonly AssembledFrontierPoint[] {
  return [...points].sort((a, b) => a.carrying_cost_rate - b.carrying_cost_rate);
}

/**
 * The frontier's category labels — the carrying-cost rate of each point, as a string.
 *
 * The rate is the axis because it is what a planner varies: each point is the same option
 * priced under a different cost of carry. Formatting is the caller's, so this stays pure
 * and locale-free; the key is identity, and two points at the same rate would be the same
 * scenario twice.
 */
export function frontierCategories(
  points: readonly AssembledFrontierPoint[],
): readonly string[] {
  return orderedFrontier(points).map((point) => String(point.carrying_cost_rate));
}

// ── the batch ────────────────────────────────────────────────────────────────

/** One option's outcome in a batch solve, already narrowed. */
export interface BatchOutcome {
  readonly optionKey: string;
  /** `null` while the request is in flight or when it succeeded. */
  readonly refusalCode: string | null;
  readonly isPending: boolean;
  readonly path: AssembledMarkdownPath | null;
}

/**
 * The grouping key a batch row sorts under.
 *
 * Three groups rather than two: a solved option, a refused option and an option still in
 * flight are three different findings, and folding "pending" into either of the others is
 * how a table comes to claim an answer it does not have yet.
 */
export const BATCH_SOLVED_GROUP = 'solved';
export const BATCH_PENDING_GROUP = 'pending';

export function batchGroupId(outcome: BatchOutcome): string {
  if (outcome.isPending) return BATCH_PENDING_GROUP;
  if (outcome.path !== null) return BATCH_SOLVED_GROUP;
  return outcome.refusalCode ?? 'unknown';
}

/**
 * The currencies the solved options' money is in, sorted and distinct. Each path carries its
 * own `currency_code` (G5: currency is data, never a constant); refused and in-flight options
 * have no path and so no currency. Pure.
 */
export function batchCurrencies(outcomes: readonly BatchOutcome[]): readonly string[] {
  const codes = new Set<string>();
  for (const outcome of outcomes) {
    if (outcome.path !== null) codes.add(outcome.path.currency_code);
  }
  return [...codes].sort();
}

export interface BatchGroup {
  readonly id: string;
  readonly outcomes: readonly BatchOutcome[];
}

/**
 * Batch rows grouped by what happened to them, largest group first.
 *
 * reqs/04 §4.4's batch answer is mostly refusals — measured over a random 500 options,
 * 38% solved and 56% refused `stock_position_absent` — so the *shape* of the refusal set
 * is the finding, and a flat list of three hundred rows states it nowhere. Grouping by the
 * platform's own code also puts every row that goes to one owner in one place: a feed that
 * has not landed, a price panel with nothing to fit against, and the optimiser's finding
 * at a real position are three different people's problems.
 *
 * Ties break on the group id so the order is stable across renders — a grid whose rows
 * reshuffle when one request settles is a grid nobody can read.
 */
export function batchGroups(outcomes: readonly BatchOutcome[]): readonly BatchGroup[] {
  const groups = new Map<string, BatchOutcome[]>();
  for (const outcome of outcomes) {
    const id = batchGroupId(outcome);
    const existing = groups.get(id);
    if (existing === undefined) groups.set(id, [outcome]);
    else existing.push(outcome);
  }
  return [...groups.entries()]
    .map(([id, rows]) => ({ id, outcomes: rows as readonly BatchOutcome[] }))
    .sort((a, b) =>
      b.outcomes.length - a.outcomes.length || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    );
}

/** Which rows a retry-failed-only action would re-ask. Indices, so the caller can refetch. */
export function failedIndices(outcomes: readonly BatchOutcome[]): readonly number[] {
  const indices: number[] = [];
  outcomes.forEach((outcome, index) => {
    if (!outcome.isPending && outcome.path === null) indices.push(index);
  });
  return indices;
}

// ── the opening position ─────────────────────────────────────────────────────

/**
 * reqs/00 D8's three states, as catalogue keys.
 *
 * The vocabulary is the platform's and this table is a rendering of it, so an unlisted
 * state is labelled `unrecognised` rather than being folded into `counted_zero` — which
 * would make a door nobody has looked at read as a door that was counted and found empty,
 * and that is the distinction the whole chapter rests on.
 */
const POSITION_STATE_KEY: Readonly<Record<string, string>> = {
  // A run's stored positions say `reported` (bata-platform `POSITION_STATE_REPORTED`): a number
  // the feed measured — the feed's own `counted`. Unlisted until 2026-10-02, so every row of a
  // run read «Uno stato che questa build non riconosce».
  reported: 'inseason.positionState.counted',
  counted: 'inseason.positionState.counted',
  counted_zero: 'inseason.positionState.countedZero',
  not_reported: 'inseason.positionState.notReported',
  not_ranged: 'inseason.positionState.notRanged',
};

export const UNRECOGNISED_POSITION_STATE_KEY = 'inseason.positionState.unrecognised';

export function toPositionStateKey(state: string): string {
  return POSITION_STATE_KEY[state] ?? UNRECOGNISED_POSITION_STATE_KEY;
}

export interface StockPositionTotals {
  readonly rows: number;
  /**
   * Units on hand where a number was reported, or `null` where NONE was.
   *
   * `null` rather than `0`, and the distinction is the point (ADR 0014): a scope in which
   * every row is unreported has an unknown position, not an empty one. A zero here would
   * be read as a cleared option and would justify a clearance nobody has evidence for.
   */
  readonly onHandUnits: number | null;
  /** How many rows carried a number at all, so the total above can be read against it. */
  readonly measuredRows: number;
  /** Rows whose partition the platform reports as stale. */
  readonly staleRows: number;
  /** Row counts by `position_state`, in descending size. */
  readonly byState: readonly { readonly state: string; readonly rows: number }[];
}

/**
 * What one scope's stock positions add up to, with the absences kept.
 *
 * `on_hand_units` is nullable on the wire and stays nullable here. Summing with a
 * `?? 0` would be the exact failure `StockPositionSummary`'s own docstring warns about —
 * "a grid that rendered an unreported door as nought units would rank it as the best-stocked
 * door in the cluster" — so an unreported row contributes nothing to the total AND is
 * counted separately, and a scope with no reported row at all has no total.
 */
export function stockPositionTotals(
  items: readonly StockPositionSummary[],
): StockPositionTotals {
  let onHand = 0;
  let measuredRows = 0;
  let staleRows = 0;
  const states = new Map<string, number>();
  for (const item of items) {
    if (item.on_hand_units !== null) {
      onHand += item.on_hand_units;
      measuredRows += 1;
    }
    if (!item.partition_is_fresh) staleRows += 1;
    states.set(item.position_state, (states.get(item.position_state) ?? 0) + 1);
  }
  return {
    rows: items.length,
    onHandUnits: measuredRows === 0 ? null : onHand,
    measuredRows,
    staleRows,
    byState: [...states.entries()]
      .map(([state, rows]) => ({ state, rows }))
      .sort((a, b) => b.rows - a.rows || (a.state < b.state ? -1 : a.state > b.state ? 1 : 0)),
  };
}

/**
 * The store-side caveat, stated on the output rather than in a footnote.
 *
 * The estate's stock feed carries no distribution-centre and no in-transit position at
 * all — `bata-data` ADR 0118 and 0210, and `fact_stock_daily.in_transit_units` reads as a
 * measured zero for all 990,045 rows because a NULL was converted on the way in. A markdown
 * opens from the SELLING position, which is the decision of 2026-09-13, so store-only is
 * the right perimeter; what would be wrong is showing the number without saying so. This is
 * the key the panels put beside every position figure.
 */
export const STORE_ONLY_PERIMETER_KEY = 'inseason.stock.storeOnlyPerimeter';
